import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import PenIcon from "@mui/icons-material/Edit";
import BoxIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CircleIcon from "@mui/icons-material/RadioButtonUnchecked";
import ArrowIcon from "@mui/icons-material/CallReceived";
import PauseIcon from "@mui/icons-material/Pause";
import PlayIcon from "@mui/icons-material/PlayArrow";
import ClearIcon from "@mui/icons-material/Clear";
import UnmuteIcon from "@mui/icons-material/Mic";
import MuteIcon from "@mui/icons-material/MicOff";
import UndoIcon from "@mui/icons-material/Undo";
import { Button, Grid, Tooltip, formControlLabelClasses } from "@mui/material";
import CallEndIcon from "@mui/icons-material/CallEnd";
import "../App.css";
import ColorPicker from "./ColorPicker";
import { styled } from "@mui/material";
import { tooltipClasses } from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

const tools = [
  { name: "brush", icon: <PenIcon /> },
  { name: "rect", icon: <BoxIcon /> },
  { name: "arc", icon: <CircleIcon /> },
  { name: "arrow", icon: <ArrowIcon /> },
  { name: "undo", icon: <UndoIcon /> },
  { name: "clear", icon: <ClearIcon /> },
];

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: "1px solid #dadde9",
  },
}));

const DrawingBoard = forwardRef(
  ({ dataChannel, isMobile, pause, pauseCB,setPaused, displaypausetext,SetPauseVideotext,pausevideotext, rtc, ...props }, ref) => {
    useImperativeHandle(ref, () => ({
      getData(response) {
        let data = JSON.parse(response);
        let x, y;
        switch (data.type) {
          case "mouse_down":
            x = data.x/0.7;
            y = data.y/0.7;
            startPainting(x, y, data.color);
            break;
          case "mouse_up":
            stopPainting();
            break;
          case "mouse_move":
            x = data.x/0.7;
            y = data.y/0.7;
            sketch(x, y);
            break;
          case "touch_down":
            x = (data.x + canvas.current.offsetLeft) / 0.7;
            y = (data.y + canvas.current.offsetTop) / 0.7;
            startPainting(x, y, data.color);
            break;
          case "touch_up":
            touchEnd();
            break;
          case "touch_move":
            x = (data.x + canvas.current.offsetLeft) / 0.7;
            y = (data.y + canvas.current.offsetTop) / 0.7;
            touchMove(x, y);
            break;
          case "tool":
            toolName.current = data.tool;
            break;
          case "clear":
            clearScreen();
            break;
          case "undo":
            undo();
            break;
          default:
            break;
        }
      },
    }));
    const canvas = useRef();
    const ctx = useRef();
    const images = useRef([]);
    const step = useRef(0);
    const coord = useRef({ x: 0, y: 0 });
    const paint = useRef(false);
    const toolName = useRef(" ");

    const [mute, setMute] = useState(false);
    const [selectedTool, setSelectedTool] = useState(" ");
    const [selectedColor, setSelectedColor] = useState("#00d084");
  
    const [hover, setHover] = useState(false);

    const onHover = () => {
      setHover(true);
    };
    const onLeave = () => {
      setHover(false);
    };

    const startPainting = (x, y, color) => {
      paint.current = true;
      coord.current.x = x - canvas.current.offsetLeft;
      coord.current.y = y - canvas.current.offsetTop;
      ctx.current.strokeStyle = color;
      ctx.current.fillStyle = color;
    };
    const touchEnd = () => {
      images.current[images.current.length - 1] = ctx.current.getImageData(
        0,
        0,
        canvas.current.width,
        canvas.current.height
      );
      paint.current = false;
    };
    const brushMove = (x, y) => {
      ctx.current.beginPath();
      ctx.current.moveTo(coord.current.x, coord.current.y);
      coord.current.x = x - canvas.current.offsetLeft;
      coord.current.y = y - canvas.current.offsetTop;
      ctx.current.lineTo(coord.current.x, coord.current.y);

      ctx.current.stroke();

 

      console.log("the brush move coordinates are", coord.current.x, coord.current.y)
    };
    const touchMove = useCallback((x, y) => {
      if (paint.current) brushMove(x, y);
    }, []);
    const stopPainting = () => {
      paint.current = false;
      images.current.push(
        ctx.current.getImageData(
          0,
          0,
          canvas.current.width,
          canvas.current.height
        )
      );
      step.current++;
    };
    const loadSavedImage = () => {
      ctx.current.clearRect(0, 0, canvas.current.width, canvas.current.height);
      if (images.current[step.current - 1])
        ctx.current.putImageData(images.current[step.current - 1], 0, 0);
    };
    const sketch = useCallback((x, y) => {
      if (paint.current) {
        switch (toolName.current) {
          case "brush":
            brushMove(x, y);
            break;
          case "arc":
            loadSavedImage();
            ctx.current.save();
            ctx.current.beginPath();
            const scalex =
              (x - canvas.current.offsetLeft - coord.current.x) / 2;
            const scaley = (y - canvas.current.offsetTop - coord.current.y) / 2;
            ctx.current.scale(scalex, scaley);
            const centerx = coord.current.x / scalex + 1;
            const centery = coord.current.y / scaley + 1;
            ctx.current.arc(centerx, centery, 1, 0, 2 * Math.PI);
            ctx.current.restore();
            ctx.current.stroke();
            break;
          case "rect":
            loadSavedImage();
            ctx.current.beginPath();
            const width = x - canvas.current.offsetLeft - coord.current.x;
            const height = y - canvas.current.offsetTop - coord.current.y;
            ctx.current.rect(coord.current.x, coord.current.y, width, height);
            ctx.current.stroke();
            break;
          case "arrow":
            loadSavedImage();
            ctx.current.beginPath();
            const headlen = 10;
            const toX = x - canvas.current.offsetLeft;
            const toY = y - canvas.current.offsetTop;
            const dx = toX - coord.current.x;
            const dy = toY - coord.current.y;
            const angle = Math.atan2(dy, dx);
            ctx.current.moveTo(coord.current.x, coord.current.y);
            ctx.current.lineTo(toX, toY);
            ctx.current.lineTo(
              toX - headlen * Math.cos(angle - Math.PI / 6),
              toY - headlen * Math.sin(angle - Math.PI / 6)
            );
            ctx.current.moveTo(toX, toY);
            ctx.current.lineTo(
              toX - headlen * Math.cos(angle + Math.PI / 6),
              toY - headlen * Math.sin(angle + Math.PI / 6)
            );
            ctx.current.stroke();
            break;
          default:
            break;
        }
      }
    }, []);
    const undo = useCallback(() => {
      if (step.current > 0) {
        step.current--;
        images.current.pop();
        loadSavedImage();
      }
    }, []);
    function clearScreen() {
      ctx.current.clearRect(0, 0, canvas.current.width, canvas.current.height);
      images.current = [];
      step.current = 0;
    }
    const mouseDown = (e) => {
      if (dataChannel && dataChannel.readyState === "open") {
        startPainting(e.clientX, e.clientY, selectedColor);
        dataChannel.send(
          JSON.stringify({
            type: "mouse_down",
            x: e.clientX - canvas.current.offsetLeft,
            y: e.clientY - canvas.current.offsetTop,
            color: selectedColor,
          })
        );
      }
    };
    const mouseUp = (e) => {
      if (dataChannel && dataChannel.readyState === "open") {
        stopPainting();
        dataChannel.send(JSON.stringify({ type: "mouse_up" }));
      }
    };
    const mouseMove = (e) => {
      if (dataChannel && dataChannel.readyState === "open") {
        sketch(e.clientX, e.clientY);
        dataChannel.send(
          JSON.stringify({
            type: "mouse_move",
            x: e.clientX - canvas.current.offsetLeft,
            y: e.clientY - canvas.current.offsetTop,
          })
        );
      }
    };
    const touchStart = (e) => {
      startPainting(e.touches[0].clientX, e.touches[0].clientY, selectedColor);
      if (dataChannel && dataChannel.readyState === "open") {
        dataChannel.send(
          JSON.stringify({
            type: "touch_down",
            x: e.touches[0].clientX,
            y: e.touches[0].clientY,
            color: selectedColor,
          })
        );
      }
    };
    const touchUp = (e) => {
      touchEnd();
      if (dataChannel && dataChannel.readyState === "open") {
        dataChannel.send(JSON.stringify({ type: "touch_up" }));
      }
    };
    const touchMovement = (e) => {
      touchMove(e.touches[0].clientX, e.touches[0].clientY);
      if (dataChannel && dataChannel.readyState === "open") {
        dataChannel.send(
          JSON.stringify({
            type: "touch_move",
            x: e.touches[0].clientX,
            y: e.touches[0].clientY,
          })
        );
      }
    };
    const changeTool = useCallback(
      (name) => {
        console.log("the pause state is", pause)

        if(!pause){
          alert("please pause the video before annotating to guide the user.");
          // document.getElementById("text").style.display = "block"
        // displaypausetext();
        // console.log("displayed in the video screen")

        
        }
        // rtc.getStats('inbound-rtp').then(res => console.log("outbound", res))
        console.log("the datachannel", dataChannel);
        if (dataChannel && dataChannel.readyState === "open") {
          if (name === "undo") {
            dataChannel.send(JSON.stringify({ type: "undo" }));
            undo();
          } else if (name === "clear") {
            dataChannel.send(JSON.stringify({ type: "clear" }));
            clearScreen();
          } else {
            toolName.current = name;
            setSelectedTool(name);
          }
        }
      },
      [dataChannel, pause]

    );

          // [dataChannel, pause, pausevideotext]

    const pauseVideo = () => {
      if (dataChannel && dataChannel.readyState === "open") {
        dataChannel.send(JSON.stringify({ type: "pause" }));
        pauseCB();
      } else {
        console.log("DataChannel not open, readyState:", dataChannel ? dataChannel.readyState : "null");
      }
    };
    const muteVideo = (args) => {
      if (dataChannel && dataChannel.readyState === "open") {
        dataChannel.send(JSON.stringify({ type: "mute", muteValue: args }));
        setMute(args);
      }
    };
    //added new items
    useEffect(() => {
      canvas.current = document.querySelector("#my-canvas");
      ctx.current = canvas.current.getContext("2d");
      let { width } = canvas.current.getBoundingClientRect();
      let toolsDiv = document.getElementById("toolbar-container");
 
      document.getElementsByTagName("body").item(0).style.height =
        window.innerHeight;
      document.getElementsByTagName("body").item(0).style.width =
        window.innerWidth;
      ctx.current.lineWidth = 2;
      ctx.current.lineCap = "round";
      ctx.current.strokeStyle = selectedColor;
      ctx.current.fillStyle = selectedColor;
      if(!isMobile){
        console.log("***************** engineer")
      canvas.current.addEventListener("mousedown", mouseDown);
      canvas.current.addEventListener("mouseup", mouseUp);
      canvas.current.addEventListener("mousemove", mouseMove);
      canvas.current.addEventListener("touchstart", touchStart);
      canvas.current.addEventListener("touchend", touchUp);
      canvas.current.addEventListener("touchmove", touchMovement);
      }
      canvas.current.oncontextmenu = function (e) {
        e.preventDefault();
      };
      return () => {
        canvas.current.removeEventListener("mousedown", mouseDown);
        canvas.current.removeEventListener("mouseup", mouseUp);
        canvas.current.removeEventListener("mousemove", mouseMove);
        canvas.current.removeEventListener("touchstart", touchStart);
        canvas.current.removeEventListener("touchend", touchUp);
        canvas.current.removeEventListener("touchmove", touchMovement);
      };
    }, [
      undo,
      sketch,
      touchMove,
      props.callDimensions,
      selectedTool,
      selectedColor,
    ]);
    useEffect(() => {
      if (dataChannel)
        dataChannel.send(
          JSON.stringify({ type: "color", color: selectedColor })
        );
    }, [selectedColor]);
    useEffect(() => {
      if (dataChannel)
        dataChannel.send(JSON.stringify({ type: "tool", tool: selectedTool }));
    }, [selectedTool]);

    return (
      <>
        

        <canvas
          id="my-canvas"
          height={props.callDimensions.height}
          width={props.callDimensions.width}
          className="drawing-board-canvas"
        />
        {!isMobile && 
          props.callDimensions.width !== 0 &&
          props.video === "video" &&
          props.callStarted && (
            <>
              <>
                <Grid
                  container
                  direction="column"
                  id="toolbar-container"
                  display="inline-block"
                  className="toolbar-container"
                >
             
                  <Grid item xs={12}>
                    {pause ? (
                      <HtmlTooltip
                        title={<Typography>Play</Typography>}
                        placement="left-start"
                      >
                        <Button
                          style={{
                            maxWidth: "50px",
                            maxHeight: "50px",
                            minWidth: "50px",
                            minHeight: "50px",
                          }}
                          onMouseEnter={onHover}
                          onMouseLeave={onLeave}
                          onClick={() => pauseVideo()}
                        >
                          <PlayIcon />
                        </Button>
                      </HtmlTooltip>
                    ) : (
                      <HtmlTooltip
                        title={<Typography>Pause</Typography>}
                        placement="left-start"
                      >
                        <Button
                          style={{
                            maxWidth: "50px",
                            maxHeight: "50px",
                            minWidth: "50px",
                            minHeight: "50px",
                          }}
                          onClick={() => pauseVideo()}
                        >
                          <PauseIcon />
                        </Button>
                      </HtmlTooltip>
                    )}
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <ColorPicker
                      strokeColor={selectedColor}
                      setStrokeColor={setSelectedColor}
                    />
                  </Grid>
                  {tools.map((tool) => (
                    <Grid
                      item
                      xs={12}
                      key={tool.name}
                      style={{
                        backgroundColor:
                          tool.name === selectedTool ? "#b9b9b9" : "inherit",
                      }}
                    >
                      <HtmlTooltip
                        title={<Typography>{tool.name}</Typography>}
                        placement="left-start"
                      >
                        <Button
                          style={{
                            maxWidth: "50px",
                            maxHeight: "50px",
                            minWidth: "50px",
                            minHeight: "50px",
                          }}
                          onClick={() => changeTool(tool.name)}
                        >
                          {tool.icon}
                        </Button>
                      </HtmlTooltip>
                    </Grid>
                  ))}
         

                  <Grid item xs={12}>
                    {mute ? (
                      <HtmlTooltip
                        title={<Typography>Mute</Typography>}
                        placement="left-start"
                      >
                        <Button
                          style={{
                            maxWidth: "50px",
                            maxHeight: "50px",
                            minWidth: "50px",
                            minHeight: "50px",
                          }}
                          onMouseEnter={onHover}
                          onMouseLeave={onLeave}
                          onClick={() => muteVideo(!mute)}
                        >
                          <MuteIcon />
                        </Button>
                      </HtmlTooltip>
                    ) : (
                      <HtmlTooltip
                        title={<Typography>Unmute</Typography>}
                        placement="left-start"
                      >
                        <Button
                          style={{
                            maxWidth: "50px",
                            maxHeight: "50px",
                            minWidth: "50px",
                            minHeight: "50px",
                          }}
                          onClick={() => muteVideo(!mute)}
                        >
                          <UnmuteIcon />
                        </Button>
                      </HtmlTooltip>
                    )}
                  </Grid>

                

                  <Grid item xs={12} style={{ padding: "3px" }}>
                    <HtmlTooltip
                      title={<Typography>End Call</Typography>}
                      placement="left-start"
                    >
                      <Button
                        style={{
                          maxWidth: "40px",
                          maxHeight: "40px",
                          minWidth: "40px",
                          minHeight: "40px",
                          backgroundColor: "#D2042D",
                        }}
                        onClick={() => props.disconnect()}
                      >
                        <CallEndIcon style={{ color: "white" }} />
                      </Button>
                    </HtmlTooltip>
                  </Grid>
                </Grid>
              </>
            </>
          )}
      </>
    );
  }
);

export default DrawingBoard;
